import { ThemedText } from "@/components/ThemedText";
import { ThemedView } from "@/components/ThemedView";
import { Button } from "@/components/ui/Button";
import CustomModal from "@/components/ui/CustomModal.web";
import { useAuth } from "@/context/AuthContext";
import { firestore } from "@/firebase/web";
import {
  addDoc,
  collection,
  serverTimestamp,
} from "firebase/firestore";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ActivityIndicator, StyleSheet } from "react-native";

interface RequestGroupAccessProps {
  groupId: string;
  groupName: string;
  pending: boolean;
}

export const RequestGroupAccess = ({
  groupId,
  groupName,
  pending,
}: RequestGroupAccessProps) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [modalInfo, setModalInfo] = useState<{
    title: string;
    message: string;
  } | null>(null);

  const handleRequest = async () => {
    if (!user) return;
    setSubmitting(true);
    try {
      await addDoc(collection(firestore, "groupAccessRequests"), {
        groupId,
        groupName,
        requesterId: user.uid,
        requesterEmail: user.email ?? "",
        // Phone-only users have no email and no displayName — fall the
        // display name back to the phone number so the admin UI's
        // existing `displayName || email || "USER"` chain shows a useful
        // identifier. requesterPhoneNumber is also stored explicitly.
        requesterDisplayName: user.displayName || user.phoneNumber || "",
        requesterPhoneNumber: user.phoneNumber ?? "",
        requesterPhotoURL: user.photoURL ?? "",
        status: "pending",
        createdAt: serverTimestamp(),
      });
      setModalInfo({
        title: t("SUCCESS"),
        message: t("ACCESS_REQUEST_SENT"),
      });
    } catch (error) {
      console.error("Error creating access request:", error);
      setModalInfo({
        title: t("ERROR"),
        message: t("FAILED_TO_REQUEST_ACCESS"),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const isPending = pending || submitting;

  return (
    <>
      <ThemedView style={styles.container}>
        <ThemedText style={styles.message}>
          {t("NOT_A_MEMBER_OF_GROUP", { groupName })}
        </ThemedText>
        {submitting ? (
          <ActivityIndicator />
        ) : (
          <Button
            testID="request-group-access-button"
            text={isPending ? t("REQUEST_UNDER_REVIEW") : t("REQUEST_ACCESS")}
            onPress={handleRequest}
            disabled={isPending}
            style={styles.button}
          />
        )}
      </ThemedView>
      {modalInfo && (
        <CustomModal
          visible={!!modalInfo}
          onClose={() => setModalInfo(null)}
          title={modalInfo.title}
        >
          <ThemedView>
            <ThemedText>{modalInfo.message}</ThemedText>
            <Button
              text={t("OK")}
              onPress={() => setModalInfo(null)}
              style={{ marginTop: 20 }}
            />
          </ThemedView>
        </CustomModal>
      )}
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 24,
    alignItems: "center",
    justifyContent: "center",
  },
  message: {
    fontSize: 18,
    textAlign: "center",
    marginBottom: 24,
  },
  button: {
    maxWidth: 320,
  },
});
